import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isImageDocument, isPdfDocument, type IDocument } from '@apis/documents';
import { useDocumentBlob, useDocumentThumbnail } from '../hooks/useDocumentBlob';
import { useObjectUrl } from '@hooks/useObjectUrl';
import { getPdfJs } from '@utils/pdfjs';
import { FileTypeIcon } from '../FileTypeIcon/FileTypeIcon';
import { formatFileSize } from '@/utils/formatFileSize';
import styles from './FilePreview.module.css';

interface IFilePreviewProps {
  siteId: string;
  doc: Pick<IDocument, 'id' | 'name' | 'sizeBytes'>;
}

export function FilePreview({ siteId, doc }: IFilePreviewProps) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const isImage = isImageDocument(doc);
  const isPdf = isPdfDocument(doc);
  const previewable = isImage || isPdf;

  // The preview image first; only without one do we fetch the full file and
  // render it ourselves (a PDF's first page is drawn by the effect below).
  const thumbnail = useDocumentThumbnail(siteId, doc.id, previewable);
  const needsFullFile = previewable && (thumbnail.data === null || thumbnail.isError);
  const { data: blob, isError: fetchFailed } = useDocumentBlob(siteId, doc.id, needsFullFile);
  const thumbnailUrl = useObjectUrl(thumbnail.data);
  const imageUrl = useObjectUrl(isImage ? blob : null);

  const [pdfReady, setPdfReady] = useState<{ fileId: string } | null>(null);
  const [renderFailedId, setRenderFailedId] = useState<string | null>(null);

  // Fallback for a PDF with no thumbnail: draw its first page with pdf.js.
  // Screen-only — nothing is saved, so it's redrawn on every open.
  // Gated on needsFullFile: `blob` can already be cached (e.g. by the markup editor).
  useEffect(() => {
    if (!isPdf || !needsFullFile || !blob) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    let pdfTask: { destroy: () => Promise<void> } | null = null;

    (async () => {
      objectUrl = URL.createObjectURL(blob);
      const pdfjsLib = await getPdfJs();
      // Closed while the library loaded: open nothing, so nothing is left behind.
      if (cancelled) return;

      const task = pdfjsLib.getDocument({ url: objectUrl });
      pdfTask = task;
      const pdf = await task.promise;
      const page = await pdf.getPage(1);
      const canvas = canvasRef.current;
      if (!canvas || cancelled) return;

      const viewport = page.getViewport({ scale: 1 });
      // Fit within the canvas element's natural CSS size (not the full container).
      const dpr = window.devicePixelRatio || 1;
      const cssWidth = canvas.clientWidth || 130;
      const cssHeight = canvas.clientHeight || 184;
      const scale = Math.min(cssWidth / viewport.width, cssHeight / viewport.height) * dpr;
      const scaled = page.getViewport({ scale });

      canvas.width = scaled.width;
      canvas.height = scaled.height;

      await page.render({ canvasContext: canvas.getContext('2d')!, canvas, viewport: scaled }).promise;
      if (!cancelled) setPdfReady({ fileId: doc.id });
    })().catch((err) => {
      if (cancelled) return;
      console.error('preview failed', err);
      setRenderFailedId(doc.id);
    });

    return () => {
      cancelled = true;
      void pdfTask?.destroy().catch(() => {});
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setPdfReady(null);
    };
  }, [isPdf, needsFullFile, blob, doc.id]);

  const currentImageUrl = thumbnailUrl ?? imageUrl;
  const currentPdfReady = pdfReady?.fileId === doc.id;
  const previewFailed = fetchFailed || renderFailedId === doc.id;
  const isLoading = previewable && !previewFailed && !currentImageUrl && !currentPdfReady;
  const noPreview = (
    <>
      <FileTypeIcon doc={doc} size={32} />
      <span className={styles.previewText}>{t('documents.noPreview')}</span>
      <span className={styles.previewSize}>{formatFileSize(doc.sizeBytes)}</span>
    </>
  );

  return (
    <div className={`${styles.previewBox}${isPdf ? ` ${styles.previewBoxPdf}` : ''}`}>
      {currentImageUrl ? (
        <img src={currentImageUrl} alt={doc.name} className={styles.previewImage} />
      ) : isPdf ? (
        <>
          <canvas
            ref={canvasRef}
            className={styles.previewCanvas}
            style={{ display: currentPdfReady ? 'block' : 'none' }}
          />
          {!currentPdfReady && !previewFailed && (
            <div
              className={`${styles.previewSkeleton} ${styles.previewSkeletonPdf}`}
              role="status"
              aria-label={t('documents.previewLoading')}
            />
          )}
          {previewFailed && (
            noPreview
          )}
        </>
      ) : isLoading ? (
        <div
          className={styles.previewSkeleton}
          role="status"
          aria-label={t('documents.previewLoading')}
        />
      ) : (
        noPreview
      )}
    </div>
  );
}